import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Camera,
  Check,
  Loader2,
  ArrowRight,
  Star,
  Sparkles,
  Calendar,
  ChevronRight,
  Shield,
  Clock,
  Image as ImageIcon
} from 'lucide-react';
import { toast, Toaster } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicOfferPage() {
  const { subdomain, slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [offer, setOffer] = useState(null);

  useEffect(() => {
    fetchOffer();
  }, [subdomain, slug]);

  const fetchOffer = async () => {
    try {
      const response = await axios.get(`${API}/public/offer/${subdomain}/${slug}`);
      setOffer(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Oferta nie zostala znaleziona');
      } else {
        setError('Wystapil blad podczas ladowania oferty');
        toast.error('Nie udalo sie zaladowac oferty');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Ladowanie oferty...</p>
        </div>
      </div>
    );
  }

  if (error || !offer) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Toaster position="top-center" />
        <Card className="max-w-md mx-4">
          <CardContent className="pt-6 text-center">
            <Camera className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-xl font-semibold mb-2">{error || 'Nie znaleziono oferty'}</h2>
            <p className="text-muted-foreground mb-6">
              Sprawdz poprawnosc linku lub skontaktuj sie z fotografem.
            </p>
            <Link to="/">
              <Button variant="outline">Strona glowna</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const {
    title,
    description,
    features = [],
    starting_price,
    packages = [],
    cta_text,
    photographer_name,
    branding = {}
  } = offer;

  const accentColor = branding.primary_color || '#18181b';
  const accentColorLight = branding.primary_color
    ? `${branding.primary_color}15`
    : 'rgba(24,24,27,0.06)';

  // Determine which package is the most expensive (recommended)
  const maxPrice = Math.max(...packages.map(p => p.price || 0));
  const recommendedIndex = packages.findIndex(p => p.price === maxPrice);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pl-PL', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  return (
    <div className="min-h-screen bg-background">
      <Toaster position="top-center" />

      {/* Header / Navbar */}
      <header className="sticky top-0 z-50 border-b border-border bg-card/80 backdrop-blur-md">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {branding.logo_url ? (
                <img
                  src={branding.logo_url}
                  alt={photographer_name}
                  className="h-8 w-auto"
                />
              ) : (
                <Camera className="w-6 h-6" style={{ color: accentColor }} />
              )}
              <span className="font-semibold text-foreground">{photographer_name}</span>
            </div>
            <Link to={`/booking/${subdomain}`}>
              <Button size="sm" style={{ backgroundColor: accentColor, color: '#fff' }}>
                <Calendar className="w-4 h-4 mr-1" />
                Umow sesje
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background gradient */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            background: `radial-gradient(ellipse at 30% 20%, ${accentColor} 0%, transparent 50%), radial-gradient(ellipse at 70% 80%, ${accentColor} 0%, transparent 50%)`
          }}
        />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-16 pb-20 sm:pt-24 sm:pb-28">
          <div className="max-w-3xl mx-auto text-center">
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Badge
                variant="secondary"
                className="mb-6 px-4 py-1.5 text-sm font-medium"
              >
                <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                Oferta specjalna
              </Badge>
            </motion.div>

            {/* Title */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground tracking-tight mb-6"
              style={{ fontFamily: branding.font_family || 'inherit' }}
            >
              {title}
            </motion.h1>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-10"
            >
              {description}
            </motion.p>

            {/* Starting Price */}
            {starting_price != null && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="inline-flex items-baseline gap-2 mb-10"
              >
                <span className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                  Ceny od
                </span>
                <span
                  className="text-4xl sm:text-5xl font-bold"
                  style={{ color: accentColor }}
                >
                  {formatPrice(starting_price)}
                </span>
                <span className="text-xl font-semibold text-muted-foreground">PLN</span>
              </motion.div>
            )}

            {/* Hero CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <Button
                size="lg"
                className="text-base px-8 h-12"
                style={{ backgroundColor: accentColor, color: '#fff' }}
                onClick={() =>
                  document.getElementById('packages')?.scrollIntoView({ behavior: 'smooth' })
                }
              >
                Zobacz pakiety
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      {features.length > 0 && (
        <section className="py-16 sm:py-20 bg-card border-y border-border">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">
                Co otrzymujesz?
              </h2>
              <p className="text-muted-foreground">
                Kazda sesja to kompleksowa usluga najwyzszej jakosci
              </p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {features.map((feature, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.07 }}
                  className="flex items-start gap-3 p-4 rounded-lg"
                  style={{ backgroundColor: accentColorLight }}
                >
                  <div
                    className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center mt-0.5"
                    style={{ backgroundColor: accentColor }}
                  >
                    <Check className="w-3.5 h-3.5 text-white" />
                  </div>
                  <span className="text-foreground font-medium">{feature}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Packages / Pricing Section */}
      {packages.length > 0 && (
        <section id="packages" className="py-16 sm:py-24">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-14"
            >
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">
                Wybierz pakiet dla siebie
              </h2>
              <p className="text-muted-foreground max-w-xl mx-auto">
                Dopasuj oferte do swoich potrzeb. Kazdy pakiet mozna dodatkowo rozbudowac.
              </p>
            </motion.div>

            <div
              className={`grid grid-cols-1 gap-6 sm:gap-8 ${
                packages.length === 1
                  ? 'max-w-md mx-auto'
                  : packages.length === 2
                  ? 'md:grid-cols-2 max-w-4xl mx-auto'
                  : packages.length >= 3
                  ? 'md:grid-cols-2 lg:grid-cols-3'
                  : ''
              }`}
            >
              {packages.map((pkg, idx) => {
                const isRecommended = idx === recommendedIndex && packages.length > 1;

                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    className="relative"
                  >
                    {/* Recommended badge */}
                    {isRecommended && (
                      <div className="absolute -top-4 left-0 right-0 flex justify-center z-10">
                        <Badge
                          className="px-4 py-1 text-sm text-white shadow-lg"
                          style={{ backgroundColor: accentColor }}
                        >
                          <Star className="w-3.5 h-3.5 mr-1.5 fill-current" />
                          Polecany
                        </Badge>
                      </div>
                    )}

                    <Card
                      className={`h-full flex flex-col transition-all duration-300 hover:shadow-lg ${
                        isRecommended
                          ? 'ring-2 shadow-md scale-[1.02] lg:scale-105'
                          : 'border-border hover:border-muted-foreground/30'
                      }`}
                      style={
                        isRecommended
                          ? { ringColor: accentColor, borderColor: accentColor }
                          : {}
                      }
                    >
                      <CardHeader className="pb-4">
                        <CardTitle className="text-xl font-bold text-foreground">
                          {pkg.name}
                        </CardTitle>
                      </CardHeader>

                      <CardContent className="flex flex-col flex-1">
                        {/* Price */}
                        <div className="mb-6">
                          <div className="flex items-baseline gap-1">
                            <span
                              className="text-4xl font-bold"
                              style={isRecommended ? { color: accentColor } : {}}
                            >
                              {formatPrice(pkg.price)}
                            </span>
                            <span className="text-lg font-medium text-muted-foreground">
                              PLN
                            </span>
                          </div>
                        </div>

                        {/* Divider */}
                        <div className="border-t border-border mb-6" />

                        {/* Package features */}
                        <ul className="space-y-3 mb-8 flex-1">
                          {(pkg.features || []).map((feature, fIdx) => (
                            <li key={fIdx} className="flex items-start gap-3">
                              <Check
                                className="w-4 h-4 mt-0.5 flex-shrink-0"
                                style={{ color: accentColor }}
                              />
                              <span className="text-sm text-foreground">{feature}</span>
                            </li>
                          ))}
                        </ul>

                        {/* CTA Button */}
                        <Link to={`/booking/${subdomain}`} className="mt-auto block">
                          <Button
                            className="w-full h-11 text-sm font-semibold"
                            variant={isRecommended ? 'default' : 'outline'}
                            style={
                              isRecommended
                                ? { backgroundColor: accentColor, color: '#fff' }
                                : {}
                            }
                          >
                            {cta_text || 'Zarezerwuj teraz'}
                            <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        </Link>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Trust / Bottom CTA Section */}
      <section
        className="py-16 sm:py-20 border-t border-border"
        style={{ backgroundColor: accentColorLight }}
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="flex justify-center gap-8 mb-10">
              <div className="flex flex-col items-center gap-2">
                <Shield className="w-6 h-6 text-muted-foreground" />
                <span className="text-xs text-muted-foreground font-medium">
                  Gwarancja jakosci
                </span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <Clock className="w-6 h-6 text-muted-foreground" />
                <span className="text-xs text-muted-foreground font-medium">
                  Terminowa realizacja
                </span>
              </div>
              <div className="flex flex-col items-center gap-2">
                <ImageIcon className="w-6 h-6 text-muted-foreground" />
                <span className="text-xs text-muted-foreground font-medium">
                  Profesjonalna obrobka
                </span>
              </div>
            </div>

            <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-4">
              Gotowy na wyjatkowe zdjecia?
            </h2>
            <p className="text-muted-foreground mb-8 max-w-lg mx-auto">
              Skontaktuj sie ze mna i umow termin sesji. Chetnie odpowiem na wszystkie pytania
              i pomoge dobrac najlepszy pakiet.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to={`/booking/${subdomain}`}>
                <Button
                  size="lg"
                  className="text-base px-8 h-12"
                  style={{ backgroundColor: accentColor, color: '#fff' }}
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  {cta_text || 'Zarezerwuj termin'}
                </Button>
              </Link>
              {starting_price != null && (
                <p className="text-sm text-muted-foreground">
                  Ceny od <strong>{formatPrice(starting_price)} PLN</strong>
                </p>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 border-t border-border bg-card">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            {branding.logo_url ? (
              <img
                src={branding.logo_url}
                alt={photographer_name}
                className="h-6 w-auto"
              />
            ) : (
              <Camera className="w-5 h-5" style={{ color: accentColor }} />
            )}
            <span className="text-sm font-medium text-foreground">{photographer_name}</span>
          </div>
          <p className="text-xs text-muted-foreground">
            Powered by{' '}
            <a href="/" className="hover:underline">
              Focus Photo
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}
